import { PieceProvenance, PiecesProvenance } from './PiecesProvenance.js';
import { ouvrirUrlSignee } from '../../../api/client.js';
import {
  Badge,
  Button,
  Dialog,
  Input,
  Price,
  PriceInput,
  Select,
  Switch,
  Textarea,
} from '@artosera/ui';
import { DEVISES, formatDate } from '@artosera/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Monogramme } from '../../../composants/Monogramme.js';
import { useContacts } from '../../../api/contacts.js';
import { estApiError } from '../../../api/errors.js';
import {
  CATEGORIES_COUT,
  MODES_ACQUISITION,
  TYPES_PROVENANCE,
  useProvenance,
  useVerifierProvenance,
  useRapportProvenance,
  useCouts,
  useAjouterProvenance,
  useModifierProvenance,
  useRetirerProvenance,
  useOrdonnerProvenance,
  useAjouterCout,
  useModifierCout,
  useRetirerCout,
  type CorpsProvenance,
  type CorpsCout,
  type ProvenancePresentee,
  type CoutPresente,
} from '../../../api/provenance.js';
import { Field } from '../../Field.js';
import styles from './ProvenanceTab.module.css';
export function ProvenanceTab({
  galerieSlug: g,
  oeuvreId: id,
  peutEcrire,
  voitLesCouts,
  coffreActif = false,
  peutDeposerPiece = false,
}: {
  galerieSlug: string;
  oeuvreId: string;
  peutEcrire: boolean;
  voitLesCouts: boolean;
  coffreActif?: boolean;
  peutDeposerPiece?: boolean;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const lang = i18n.language === 'en' ? 'en' : 'fr';
  const tr = (key: string, values?: Record<string, unknown>) =>
    t(`regie:oeuvres.provenance.${key}`, values);
  const lecture = useProvenance(g, id);
  const verifier = useVerifierProvenance(g, id);
  const rapport = useRapportProvenance(g, id);
  const [verification, setVerification] = useState<{
    id: string;
    etat: 'verifiee' | 'contestee';
    note: string;
  } | null>(null);
  const couts = useCouts(g, id, voitLesCouts);
  const ajouter = useAjouterProvenance(g, id),
    modifier = useModifierProvenance(g, id),
    retirer = useRetirerProvenance(g, id),
    ordonner = useOrdonnerProvenance(g, id);
  const ajouterCout = useAjouterCout(g, id),
    modifierCout = useModifierCout(g, id),
    retirerCout = useRetirerCout(g, id);
  const [erreur, setErreur] = useState<string | null>(null);
  const [edition, setEdition] = useState<string | null>(null);
  const [corps, setCorps] = useState<CorpsProvenance | null>(null);
  const [recherche, setRecherche] = useState('');
  const [depotPieceEnCours, setDepotPieceEnCours] = useState(false);
  const contacts = useContacts(g, { q: recherche, parPage: 8 }, { enabled: corps !== null });
  const [cout, setCout] = useState<CorpsCout | null>(null),
    [coutId, setCoutId] = useState<string | null>(null);
  const [suppression, setSuppression] = useState<{ id: string; cout: boolean } | null>(null);
  const entrees = lecture.data?.entrees ?? [];
  async function geste(fn: () => Promise<unknown>) {
    setErreur(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
      return false;
    }
  }
  function editer(p?: ProvenancePresentee) {
    setEdition(p?.id ?? null);
    setRecherche('');
    setCorps(
      p
        ? {
            type: p.type,
            libelle: p.libelle,
            contactId: p.contactId,
            periode: p.periode,
            mode: p.mode,
            source: p.source,
            public: p.public,
            note: p.note,
            ...(coffreActif ? { pieces: p.pieces ?? [] } : {}),
          }
        : { type: 'proprietaire', libelle: '', contactId: null, public: false },
    );
  }
  function editerCout(p?: CoutPresente) {
    setCoutId(p?.id ?? null);
    setCout(
      p
        ? {
            categorie: p.categorie,
            libelle: p.libelle,
            montantCentimes: p.montantCentimes,
            devise: p.devise,
            date: p.date,
            fournisseur: p.fournisseur,
            note: p.note,
          }
        : { categorie: 'fabrication', libelle: '', montantCentimes: 0, devise: 'EUR' },
    );
  }
  async function deplacer(index: number, delta: number) {
    const ids = entrees.map((p) => p.id);
    const destination = index + delta;
    [ids[index], ids[destination]] = [ids[destination]!, ids[index]!];
    await geste(() => ordonner.mutateAsync(ids));
  }
  return (
    <div className={styles.page} data-ui="artwork-provenance">
      <section className={styles.section}>
        <div className={styles.entete}>
          <h2>{tr('titre')}</h2>
          <Button
            variant="secondary"
            loading={rapport.isPending}
            onClick={() =>
              void geste(async () => {
                const r = await rapport.mutateAsync();
                ouvrirUrlSignee(r.url);
              })
            }
          >
            {tr('rapport')}
          </Button>
          {peutEcrire && (
            <Button variant="secondary" onClick={() => editer()}>
              {tr('ajouterEtape')}
            </Button>
          )}
        </div>
        {lecture.data?.provenance && <p>{tr('resume', lecture.data.provenance)}</p>}
        {lecture.isLoading ? (
          <p>{t('commun:chargement')}</p>
        ) : lecture.isError ? (
          <p role="alert">
            {estApiError(lecture.error) ? lecture.error.message : t('commun:erreurReseau')}
          </p>
        ) : entrees.length === 0 ? (
          <p>{tr('vide')}</p>
        ) : (
          <ol className={styles.liste}>
            {entrees.map((p, index) => (
              <li className={styles.etape} key={p.id}>
                <div className={styles.ligne}>
                  <Badge>{tr(`types.${p.type}`)}</Badge>
                  <strong className={styles.texte}>{p.libelle}</strong>
                  {p.public && <Badge>{tr('publique')}</Badge>}
                </div>
                <p className={styles.meta}>
                  {[p.periode, p.mode ? tr(`modes.${p.mode}`) : '', p.source]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {p.note && <p className={styles.meta}>{p.note}</p>}
                <Badge>{tr(`verification.${p.verification?.etat ?? 'a_verifier'}`)}</Badge>
                {p.verification && (
                  <p className={styles.meta}>
                    {p.verification.par} · {formatDate(new Date(p.verification.le), lang)} ·{' '}
                    {p.verification.note}
                  </p>
                )}
                {p.piecesMasquees ? (
                  <p>{tr('piecesMasquees')}</p>
                ) : (
                  coffreActif && (
                    <div>
                      {(p.pieces ?? []).map((piece) => (
                        <PieceProvenance key={piece} galerie={g} id={piece} />
                      ))}
                    </div>
                  )
                )}
                {peutEcrire && (
                  <div className={styles.gestes}>
                    {(['verifiee', 'contestee'] as const).map((etat) => (
                      <Button
                        key={etat}
                        variant="ghost"
                        onClick={() => setVerification({ id: p.id, etat, note: '' })}
                      >
                        {tr(`marquer.${etat}`)}
                      </Button>
                    ))}
                    <Button
                      variant="ghost"
                      disabled={index === 0 || ordonner.isPending}
                      aria-label={tr('monter', { nom: p.libelle })}
                      onClick={() => void deplacer(index, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={index === entrees.length - 1 || ordonner.isPending}
                      aria-label={tr('descendre', { nom: p.libelle })}
                      onClick={() => void deplacer(index, 1)}
                    >
                      ↓
                    </Button>
                    <Button variant="ghost" onClick={() => editer(p)}>
                      {t('commun:actions.modifier')}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setSuppression({ id: p.id, cout: false })}
                    >
                      {tr('retirer')}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
      <section className={styles.section} data-ui="artwork-owners">
        <div className={styles.entete}>
          <h2>{tr('historique')}</h2>
        </div>
        <ol className={styles.frise}>
          {entrees
            .filter((p) => p.type === 'proprietaire')
            .map((p) => (
              <li key={p.id} className={styles.ligne}>
                {p.contactId && <Monogramme nom={p.contactNom ?? p.libelle} taille={36} />}
                <div>
                  {p.contactId ? (
                    <Link to={`/${g}/contacts/${p.contactId}`}>{p.contactNom ?? p.libelle}</Link>
                  ) : (
                    p.libelle
                  )}
                  <p className={styles.meta}>{p.periode}</p>
                </div>
              </li>
            ))}
        </ol>
        <h3>{tr('ventesConnues')}</h3>
        {(lecture.data?.ventesConnues ?? [])
          .filter((v) => !entrees.some((p) => p.contactId === v.contactId))
          .map((v, index) => (
            <div className={styles.ligne} key={`${v.contactId}-${index}`}>
              <span className={styles.texte}>
                {v.contactNom} · {formatDate(new Date(v.date), lang, 'court')} {v.reference}
              </span>
              {peutEcrire && (
                <Button
                  variant="secondary"
                  loading={ajouter.isPending}
                  onClick={() =>
                    void geste(() =>
                      ajouter.mutateAsync({
                        type: 'proprietaire',
                        libelle: v.contactNom,
                        contactId: v.contactId,
                        mode: 'achat',
                        source: 'vente de la galerie',
                        public: false,
                      }),
                    )
                  }
                >
                  {tr('ajouterVente', { nom: v.contactNom })}
                </Button>
              )}
            </div>
          ))}
      </section>
      {voitLesCouts && (
        <section className={styles.section} data-ui="artwork-production-costs">
          <div className={styles.entete}>
            <h2>{tr('coutsTitre')}</h2>
            {peutEcrire && (
              <Button variant="secondary" onClick={() => editerCout()}>
                {tr('ajouterCout')}
              </Button>
            )}
          </div>
          {couts.isError ? (
            <p role="alert">
              {estApiError(couts.error) ? couts.error.message : t('commun:erreurReseau')}
            </p>
          ) : (
            <div className={styles.table}>
              <table>
                <thead>
                  <tr>
                    {['categorie', 'libelle', 'fournisseur', 'date', 'montant'].map((k) => (
                      <th key={k}>{tr(k)}</th>
                    ))}
                    {peutEcrire && <th>{tr('actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {(couts.data?.couts ?? []).map((p) => (
                    <tr key={p.id}>
                      <td>{tr(`categories.${p.categorie}`)}</td>
                      <td>{p.libelle}</td>
                      <td>{p.fournisseur}</td>
                      <td>
                        {p.date ? formatDate(new Date(`${p.date}T12:00:00Z`), lang, 'court') : '—'}
                      </td>
                      <td>
                        <Price cents={p.montantCentimes} currency={p.devise} lang={lang} />
                      </td>
                      {peutEcrire && (
                        <td>
                          <Button variant="ghost" onClick={() => editerCout(p)}>
                            {t('commun:actions.modifier')}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setSuppression({ id: p.id, cout: true })}
                          >
                            {tr('retirer')}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {(couts.data?.totaux ?? []).map((p) => (
                    <tr key={p.devise}>
                      <th colSpan={4}>
                        {tr('total')} {p.devise}
                      </th>
                      <td>
                        <Price cents={p.totalCentimes} currency={p.devise} lang={lang} />
                      </td>
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          )}
        </section>
      )}
      {erreur && (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      )}
      <Dialog
        open={corps !== null}
        onOpenChange={(o) => {
          if (!o && !depotPieceEnCours) setCorps(null);
        }}
        title={edition ? tr('modifierEtape') : tr('ajouterEtape')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            disabled={!corps?.libelle.trim() || depotPieceEnCours}
            loading={ajouter.isPending || modifier.isPending}
            onClick={() => {
              if (corps)
                void geste(() =>
                  edition
                    ? modifier.mutateAsync({ id: edition, corps })
                    : ajouter.mutateAsync(corps),
                ).then((ok) => {
                  if (ok) setCorps(null);
                });
            }}
          >
            {t('commun:actions.enregistrer')}
          </Button>
        }
      >
        {corps && (
          <div className={styles.formulaire}>
            <Field label={tr('type')} htmlFor="p-type">
              <Select
                id="p-type"
                value={corps.type}
                onValueChange={(v) => setCorps({ ...corps, type: v as CorpsProvenance['type'] })}
                options={TYPES_PROVENANCE.map((v) => ({ value: v, label: tr(`types.${v}`) }))}
              />
            </Field>
            <Field label={tr('libelle')} htmlFor="p-libelle">
              <Input
                id="p-libelle"
                maxLength={300}
                value={corps.libelle}
                onChange={(e) => setCorps({ ...corps, libelle: e.target.value })}
              />
            </Field>
            <Field label={tr('contactRecherche')} htmlFor="p-recherche">
              <Input
                id="p-recherche"
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
              />
              <Select
                aria-label={tr('contact')}
                value={corps.contactId ?? ''}
                onValueChange={(v) => setCorps({ ...corps, contactId: v || null })}
                options={[
                  { value: '', label: tr('sansContact') },
                  ...(contacts.data?.contacts ?? []).map((c) => ({
                    value: c.id,
                    label: c.nomAffiche,
                  })),
                  ...(corps.contactId &&
                  !contacts.data?.contacts.some((c) => c.id === corps.contactId)
                    ? [
                        {
                          value: corps.contactId,
                          label:
                            entrees.find((p) => p.id === edition)?.contactNom ?? corps.contactId,
                        },
                      ]
                    : []),
                ]}
              />
            </Field>
            {(['periode', 'source', 'note'] as const).map((k) => (
              <Field label={tr(k)} htmlFor={`p-${k}`} key={k}>
                <Input
                  id={`p-${k}`}
                  maxLength={k === 'periode' ? 120 : k === 'note' ? 2000 : 300}
                  value={corps[k] ?? ''}
                  onChange={(e) => setCorps({ ...corps, [k]: e.target.value })}
                />
              </Field>
            ))}
            <Field label={tr('mode')} htmlFor="p-mode">
              <Select
                id="p-mode"
                value={corps.mode ?? ''}
                onValueChange={(v) =>
                  setCorps({ ...corps, mode: (v || null) as CorpsProvenance['mode'] })
                }
                options={[
                  { value: '', label: '—' },
                  ...MODES_ACQUISITION.map((v) => ({ value: v, label: tr(`modes.${v}`) })),
                ]}
              />
            </Field>
            {coffreActif && (
              <PiecesProvenance
                galerie={g}
                oeuvreId={id}
                pieces={corps.pieces ?? []}
                peutDeposer={peutDeposerPiece}
                onDepotEnCours={setDepotPieceEnCours}
                onChange={(pieces) => setCorps({ ...corps, pieces })}
              />
            )}
            <Switch
              label={tr('imprimer')}
              checked={corps.public ?? false}
              onCheckedChange={(v) => setCorps({ ...corps, public: v })}
            />
            {erreur && <p role="alert">{erreur}</p>}
          </div>
        )}
      </Dialog>
      <Dialog
        open={verification !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setVerification(null);
        }}
        title={tr('verificationTitre')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            loading={verifier.isPending}
            onClick={() => {
              if (verification)
                void geste(() => verifier.mutateAsync(verification)).then((ok) => {
                  if (ok) setVerification(null);
                });
            }}
          >
            {t('commun:actions.enregistrer')}
          </Button>
        }
      >
        {verification && (
          <label>
            {tr('noteVerification')}
            <Input
              maxLength={300}
              value={verification.note}
              onChange={(e) => setVerification({ ...verification, note: e.target.value })}
            />
          </label>
        )}
        {erreur && <p role="alert">{erreur}</p>}
      </Dialog>
      <Dialog
        open={cout !== null}
        onOpenChange={(o) => {
          if (!o) setCout(null);
        }}
        title={coutId ? tr('modifierCout') : tr('ajouterCout')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            disabled={!cout?.libelle.trim()}
            loading={ajouterCout.isPending || modifierCout.isPending}
            onClick={() => {
              if (cout)
                void geste(() =>
                  coutId
                    ? modifierCout.mutateAsync({ id: coutId, corps: cout })
                    : ajouterCout.mutateAsync(cout),
                ).then((ok) => {
                  if (ok) setCout(null);
                });
            }}
          >
            {t('commun:actions.enregistrer')}
          </Button>
        }
      >
        {cout && (
          <div className={styles.formulaire}>
            <Field label={tr('categorie')} htmlFor="c-categorie">
              <Select
                id="c-categorie"
                value={cout.categorie}
                onValueChange={(v) => setCout({ ...cout, categorie: v as CorpsCout['categorie'] })}
                options={CATEGORIES_COUT.map((v) => ({ value: v, label: tr(`categories.${v}`) }))}
              />
            </Field>
            {(['libelle', 'fournisseur', 'note'] as const).map((k) => (
              <Field label={tr(k)} htmlFor={`c-${k}`} key={k}>
                <Textarea
                  id={`c-${k}`}
                  maxLength={k === 'note' ? 2000 : k === 'fournisseur' ? 200 : 300}
                  value={cout[k] ?? ''}
                  onChange={(e) => setCout({ ...cout, [k]: e.target.value })}
                />
              </Field>
            ))}
            <Field label={tr('montant')} htmlFor="c-montant">
              <PriceInput
                id="c-montant"
                cents={cout.montantCentimes}
                onCentsChange={(v) => setCout({ ...cout, montantCentimes: v })}
                currency={cout.devise ?? 'EUR'}
                lang={lang}
              />
            </Field>
            <Field label={tr('devise')} htmlFor="c-devise">
              <Select
                id="c-devise"
                value={cout.devise ?? 'EUR'}
                onValueChange={(v) => setCout({ ...cout, devise: v as CorpsCout['devise'] })}
                options={DEVISES.map((v) => ({ value: v, label: v }))}
              />
            </Field>
            <Field label={tr('date')} htmlFor="c-date">
              <Input
                id="c-date"
                type="date"
                value={cout.date ?? ''}
                onChange={(e) => setCout({ ...cout, date: e.target.value || null })}
              />
            </Field>
            {erreur && <p role="alert">{erreur}</p>}
          </div>
        )}
      </Dialog>
      <Dialog
        open={suppression !== null}
        onOpenChange={(o) => {
          if (!o) setSuppression(null);
        }}
        title={tr('retirerConfirmation')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            variant="danger"
            loading={retirer.isPending || retirerCout.isPending}
            onClick={() => {
              if (suppression)
                void geste(() =>
                  suppression.cout
                    ? retirerCout.mutateAsync(suppression.id)
                    : retirer.mutateAsync(suppression.id),
                ).then((ok) => {
                  if (ok) setSuppression(null);
                });
            }}
          >
            {tr('retirer')}
          </Button>
        }
      >
        {erreur && <p role="alert">{erreur}</p>}
      </Dialog>
    </div>
  );
}
